/**
 * Auto-Fill Service
 * Applies parsed PDF data to form fields with intelligent field matching
 */

export interface AutoFillResult {
  success: boolean
  fieldsApplied: string[]
  fieldsSkipped: string[]
  totalFields: number
  message: string
}

export class AutoFillService {
  
  /**
   * Apply parsed data to form with smart field matching
   */
  static applyParsedDataToForm(
    parsedData: any, 
    currentFormData: any
  ): { updatedFormData: any; result: AutoFillResult } {
    console.log('🚀 Starting auto-fill process...')
    console.log('📊 Parsed data received:', parsedData)
    console.log('📋 Current form data:', Object.keys(currentFormData))

    const updatedData = { ...currentFormData }
    const fieldsApplied: string[] = []
    const fieldsSkipped: string[] = []
    let totalFields = 0

    // Use flattened data if available (preferred for auto-fill)
    const sourceData = parsedData.flattenedForAutoFill || 
                      this.flattenParsedData(parsedData)

    console.log('🎯 Auto-fill source data:', sourceData)

    // Apply each field from parsed data
    Object.entries(sourceData).forEach(([fieldName, fieldValue]) => {
      totalFields++
      
      if (this.shouldApplyField(fieldName, fieldValue, currentFormData)) {
        let processedValue = this.cleanFieldValue(fieldValue)
        
        // Convert date format if this is a date field
        if (this.isDateField(fieldName)) {
          processedValue = this.convertDateFormat(processedValue)
          console.log(`🗓️  Processing date field "${fieldName}": "${fieldValue}" → "${processedValue}"`)
        }
        
        updatedData[fieldName] = processedValue
        fieldsApplied.push(fieldName)
        
        if (fieldName.startsWith('owner2_') || fieldName.startsWith('owner3_')) {
          console.log(`🎯 Applied additional owner field: ${fieldName} = "${processedValue}"`)
        } else {
          console.log(`✅ Applied: ${fieldName} = "${processedValue}"`)
        }
      } else {
        fieldsSkipped.push(fieldName)
        console.log(`⏭️  Skipped: ${fieldName} = "${fieldValue}" (${this.getSkipReason(fieldName, fieldValue, currentFormData)})`)
      }
    })

    const result: AutoFillResult = {
      success: fieldsApplied.length > 0,
      fieldsApplied,
      fieldsSkipped,
      totalFields,
      message: `Applied ${fieldsApplied.length} of ${totalFields} fields`
    }

    console.log('🎉 Auto-fill completed:', result)
    
    return { updatedFormData: updatedData, result }
  }

  /**
   * Flatten parsed data for auto-fill if not already flattened
   */
  private static flattenParsedData(parsedData: any): { [key: string]: string } {
    const flattened: { [key: string]: string } = {}

    // Handle structured data from AI parsing
    if (parsedData && typeof parsedData === 'object') {
      Object.values(parsedData).forEach(section => {
        if (section && typeof section === 'object') {
          Object.entries(section).forEach(([key, value]) => {
            if (value && typeof value === 'string' && value.trim()) {
              flattened[key] = value.trim()
            }
          })
        }
      })
    }

    return flattened
  }

  /**
   * Determine if a field should be applied to the form
   */
  private static shouldApplyField(
    fieldName: string, 
    fieldValue: any, 
    currentFormData: any
  ): boolean {
    // Skip if no value
    if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
      return false
    }

    // Special handling for additional owner fields (owner2_, owner3_)
    // These fields don't exist in the initial form state but should be added
    const isAdditionalOwnerField = fieldName.startsWith('owner2_') || fieldName.startsWith('owner3_')
    
    // Skip if field doesn't exist in form (not a form field), EXCEPT for additional owner fields
    if (!isAdditionalOwnerField && !(fieldName in currentFormData)) {
      return false
    }

    // Skip if field already has a value (don't overwrite user input)
    const currentValue = currentFormData[fieldName]
    if (currentValue && typeof currentValue === 'string' && currentValue.trim()) {
      return false
    }

    // Skip metadata fields
    const metadataFields = [
      'rawExtractedData', 
      'extractionMetadata', 
      'translatedData',
      'flattenedForAutoFill'
    ]
    if (metadataFields.includes(fieldName)) {
      return false
    }

    return true
  }

  /**
   * Clean and format field value for form input
   */
  private static cleanFieldValue(value: any): string {
    if (typeof value !== 'string') {
      return String(value)
    }

    let cleanedValue = value
      .trim()
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/[""'']/g, '"') // Normalize quotes

    return cleanedValue
  }

  /**
   * Converts a date from MM/DD/YYYY or MM/DD/YY format to YYYY-MM-DD format for HTML5 date inputs
   */
  private static convertDateFormat(dateString: string): string {
    if (!dateString || typeof dateString !== 'string') {
      return dateString
    }

    // Match MM/DD/YYYY or MM/DD/YY format
    const dateMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
    if (!dateMatch) {
      return dateString // Return as-is if not recognized format
    }

    const [, month, day, year] = dateMatch
    
    // Handle 2-digit year (assume 20xx for years 00-29, 19xx for years 30-99)
    let fullYear = year
    if (year.length === 2) {
      const yearNum = parseInt(year)
      fullYear = yearNum <= 29 ? `20${year}` : `19${year}`
    }

    // Pad month and day with zeros if needed
    const paddedMonth = month.padStart(2, '0')
    const paddedDay = day.padStart(2, '0')

    const convertedDate = `${fullYear}-${paddedMonth}-${paddedDay}`
    console.log(`📅 Date conversion: "${dateString}" → "${convertedDate}"`)
    
    return convertedDate
  }

  /**
   * Determines if a field is a date field based on its name
   */
  private static isDateField(fieldName: string): boolean {
    const dateFieldIndicators = [
      'date', 'birthday', 'birthdate', 'startdate', 'enddate', 
      'dateofbirth', 'businessstartdate', 'contactdateofbirth'
    ]
    
    const lowerFieldName = fieldName.toLowerCase()
    return dateFieldIndicators.some(indicator => lowerFieldName.includes(indicator))
  }

  /**
   * Get reason why a field was skipped (for debugging)
   */
  private static getSkipReason(
    fieldName: string, 
    fieldValue: any, 
    currentFormData: any
  ): string {
    if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
      return 'empty value'
    }

    if (!(fieldName in currentFormData)) {
      return 'not a form field'
    }

    const currentValue = currentFormData[fieldName]
    if (currentValue && typeof currentValue === 'string' && currentValue.trim()) {
      return 'field already filled'
    }

    const metadataFields = [
      'rawExtractedData', 
      'extractionMetadata', 
      'translatedData',
      'flattenedForAutoFill'
    ]
    if (metadataFields.includes(fieldName)) {
      return 'metadata field'
    }

    return 'unknown'
  }

  /**
   * Generate auto-fill report for user feedback
   */
  static generateAutoFillReport(result: AutoFillResult): string {
    const { fieldsApplied, fieldsSkipped, totalFields } = result

    if (fieldsApplied.length === 0) {
      return '❌ No fields could be auto-filled from the PDF'
    }

    let report = `✅ Auto-filled ${fieldsApplied.length} fields:\n`
    fieldsApplied.forEach(field => {
      report += `  • ${field}\n`
    })

    if (fieldsSkipped.length > 0) {
      report += `\n⏭️  Skipped ${fieldsSkipped.length} fields (already filled or not applicable)`
    }

    return report
  }

  /**
   * Validate auto-fill results
   */
  static validateAutoFillResults(result: AutoFillResult): { isValid: boolean; warnings: string[] } {
    const warnings: string[] = []

    if (result.totalFields === 0) {
      warnings.push('No fields found in parsed data')
    }

    if (result.fieldsApplied.length === 0) {
      warnings.push('No fields were auto-filled - check if form fields match database field names')
    }

    const fillRate = result.fieldsApplied.length / result.totalFields
    if (fillRate < 0.3) {
      warnings.push(`Low fill rate (${Math.round(fillRate * 100)}%) - consider checking field name mappings`)
    }

    return {
      isValid: warnings.length === 0,
      warnings
    }
  }
}
